import { findReferenceList } from "@/lib/tool-kit/text-analysis/sections";
import type { TextSpan } from "@/lib/tool-kit/text-analysis/segment";

/**
 * Abbreviations and acronyms in a draft, checked against APA's guidance (APA Style
 * Abbreviations Guide, 2022, and Numbers and Statistics Guide, 2024):
 *
 * - define an abbreviation at first use — "response time (RT)", or "(response time
 *   [RT])" inside parentheses;
 * - use an abbreviation at least three times, or spell the term out each time;
 * - use it consistently — do not switch back to the spelled-out term;
 * - abbreviations listed as words in the dictionary (APA's examples: AIDS, IQ) and
 *   statistical abbreviations such as SD and OR need no definition.
 *
 * Definitions are found with the Schwartz & Hearst (2003) algorithm: walk the
 * abbreviation's letters right to left through the words before the parenthesis, and
 * require its first letter to start a word.
 */

export type AbbreviationIssue =
  | "undefined"
  | "before-definition"
  | "multiple-definitions"
  | "few-uses"
  | "respelled";

export type SkipReason = "dictionary" | "statistic" | "ignored" | "roman";

export interface AbbreviationOccurrence extends TextSpan {
  /** The abbreviation without a plural "s": "NGO" for "NGOs". */
  base: string;
}

export interface AbbreviationDefinition extends TextSpan {
  /** The abbreviation token inside the definition. */
  abbr: TextSpan;
  longForm: string;
}

export interface AbbreviationEntry {
  abbr: string;
  /** Every use outside a definition. */
  uses: AbbreviationOccurrence[];
  definitions: AbbreviationDefinition[];
  usesAfterDefinition: number;
  /** The spelled-out term again after the first definition. */
  respelled: TextSpan[];
  issues: AbbreviationIssue[];
}

export interface SkippedAbbreviation {
  abbr: string;
  reason: SkipReason;
  count: number;
}

export interface AbbreviationReport {
  /** In order of first appearance. */
  entries: AbbreviationEntry[];
  skipped: SkippedAbbreviation[];
}

export interface AbbreviationOptions {
  /** Abbreviations the writer has told the checker to leave alone. */
  ignore: ReadonlySet<string>;
  ignoreReferences: boolean;
  /** Skip the dictionary-word and statistical abbreviations APA lets you leave undefined. */
  skipCommon: boolean;
}

/** APA: abbreviations entered as words in the dictionary need no definition. */
export const DICTIONARY_ABBREVIATIONS: readonly string[] = [
  "AIDS",
  "HIV",
  "IQ",
  "REM",
];

/** APA: do not define abbreviations that represent statistics (two-letter ones here). */
export const STATISTICAL_ABBREVIATIONS: readonly string[] = ["OR", "SD"];

/** APA: use an abbreviation at least three times, or write the term out. */
export const MIN_USES = 3;

const TOKEN_RE = /[\p{L}\p{N}&]+(?:-[\p{L}\p{N}&]+)*/gu;
const SEGMENT_RE = /[^-]+/g;
const LETTER_RE = /\p{L}/u;
const UPPER_RE = /\p{Lu}/u;
const LOWER_RE = /\p{Ll}/u;
const UPPER_OR_DIGIT_RE = /[\p{Lu}\p{N}]/u;
const DIGITS_RE = /^\d+$/;
const STARTS_LETTER_RE = /^\p{L}/u;
const ALNUM_RE = /[\p{L}\p{N}]/u;
const ROMAN_RE = /^(?=[IVX]{2})X{0,3}(?:IX|IV|V?I{0,3})$/;
const SPACE_RE = /\s/;
const HARD_STOP_RE = /[!?;:()[\]]/;
const LINE_RE = /[^\n]+/g;
const WORD_IN_WINDOW_RE = /\S+/g;
const EDGE_PUNCT_RE = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
const REGEX_SPECIAL_RE = /[.*+?^${}()|[\]\\]/g;
const SPACES_RE = /\s+/g;
const LIST_SEPARATOR_RE = /[,;]/;
const SENTENCE_END_RE = /[.!?]/;

/* ----------------------------------------------------------------- detection */

/** "NGOs" → "NGO"; "mRNA" → "mRNA"; "McDonald" → null (mostly lowercase). */
export function abbreviationBase(segment: string): string | null {
  if (!STARTS_LETTER_RE.test(segment)) {
    return null;
  }
  const plural =
    segment.length >= 3 &&
    segment.endsWith("s") &&
    UPPER_OR_DIGIT_RE.test(segment.at(-2) ?? "");
  const core = plural ? segment.slice(0, -1) : segment;
  let letters = 0;
  let upper = 0;
  for (const ch of core) {
    if (LETTER_RE.test(ch)) {
      letters++;
      if (UPPER_RE.test(ch)) {
        upper++;
      }
    }
  }
  return upper >= 2 && upper * 2 >= letters ? core : null;
}

/**
 * The abbreviations inside one hyphenated token: "COVID-19-related" holds "COVID-19",
 * "SARS-CoV-2" is one abbreviation, "HIV-positive" holds "HIV".
 */
function abbreviationsInToken(
  token: string,
  offset: number
): AbbreviationOccurrence[] {
  const segments = [...token.matchAll(SEGMENT_RE)].map((m) => ({
    text: m[0],
    start: m.index,
  }));
  const found: AbbreviationOccurrence[] = [];
  let i = 0;
  while (i < segments.length) {
    const first = abbreviationBase(segments[i].text);
    if (first === null) {
      i++;
      continue;
    }
    const parts = [first];
    let j = i;
    while (j + 1 < segments.length) {
      const next = segments[j + 1].text;
      const part = DIGITS_RE.test(next) ? next : abbreviationBase(next);
      if (part === null) {
        break;
      }
      parts.push(part);
      j++;
    }
    found.push({
      start: offset + segments[i].start,
      end: offset + segments[j].start + segments[j].text.length,
      base: parts.join("-"),
    });
    i = j + 1;
  }
  return found;
}

/** Lines written entirely in capitals (headings) are not scanned for abbreviations. */
function capitalLines(text: string, limit: number): TextSpan[] {
  const spans: TextSpan[] = [];
  for (const match of text.slice(0, limit).matchAll(LINE_RE)) {
    const line = match[0];
    let letters = 0;
    for (const ch of line) {
      if (LETTER_RE.test(ch)) {
        letters++;
      }
    }
    if (letters >= 4 && !LOWER_RE.test(line)) {
      spans.push({ start: match.index, end: match.index + line.length });
    }
  }
  return spans;
}

/**
 * Unit symbols with two or more capitals. APA abbreviates a unit after a number without
 * defining it ("80 m", "16–30 kHz"); most unit symbols have a single capital and are
 * never detected, so only these need recognising — and only after a number, because
 * "in 2020 WHO declared" must still count WHO.
 */
const UNIT_SYMBOLS = new Set([
  "KB",
  "MB",
  "GB",
  "TB",
  "PB",
  "MHz",
  "GHz",
  "THz",
  "MW",
  "GW",
  "TW",
  "MWh",
  "GWh",
  "TWh",
  "MPa",
  "GPa",
  "MeV",
  "GeV",
  "TeV",
  "MBq",
  "GBq",
]);

/** "5 GB", "2.4 GHz": a unit symbol after a number, not an abbreviation to define. */
function isUnitAfterNumber(text: string, occurrence: AbbreviationOccurrence) {
  if (!UNIT_SYMBOLS.has(occurrence.base)) {
    return false;
  }
  let i = occurrence.start - 1;
  while (i >= 0 && SPACE_RE.test(text[i])) {
    i--;
  }
  return i >= 0 && DIGITS_RE.test(text[i]);
}

function scanOccurrences(text: string, limit: number) {
  const skipLines = capitalLines(text, limit);
  const occurrences: AbbreviationOccurrence[] = [];
  for (const match of text.slice(0, limit).matchAll(TOKEN_RE)) {
    for (const occurrence of abbreviationsInToken(match[0], match.index)) {
      const inCapitals = skipLines.some(
        (line) => occurrence.start >= line.start && occurrence.end <= line.end
      );
      if (!(inCapitals || isUnitAfterNumber(text, occurrence))) {
        occurrences.push(occurrence);
      }
    }
  }
  return occurrences;
}

/* ---------------------------------------------------------------- definitions */

/**
 * Schwartz & Hearst's best long form: match the short form's characters right to left
 * through the candidate, the first character at the start of a word. Returns the long
 * form, or null when the letters do not all appear in order.
 */
export function bestLongForm(short: string, candidate: string): string | null {
  let s = short.length - 1;
  let l = candidate.length - 1;
  while (s >= 0) {
    const c = short[s].toLowerCase();
    if (!ALNUM_RE.test(c)) {
      s--;
      continue;
    }
    while (
      (l >= 0 && candidate[l].toLowerCase() !== c) ||
      (s === 0 && l > 0 && ALNUM_RE.test(candidate[l - 1]))
    ) {
      l--;
    }
    if (l < 0) {
      return null;
    }
    l--;
    s--;
  }
  return candidate.slice(candidate.lastIndexOf(" ", l) + 1);
}

function closingIndex(text: string, open: number): number {
  const closer = text[open] === "(" ? ")" : "]";
  const limit = Math.min(text.length, open + 200);
  for (let i = open + 1; i < limit; i++) {
    if (text[i] === closer) {
      return i;
    }
    if (text[i] === text[open]) {
      return -1;
    }
  }
  return -1;
}

/** Where a long form could start: back to the clause's start, never across a sentence. */
function windowStart(text: string, before: number): number {
  let i = before - 1;
  while (i >= 0) {
    const c = text[i];
    if (HARD_STOP_RE.test(c) || c === "\n") {
      return i + 1;
    }
    const sentenceEnd =
      c === "." &&
      SPACE_RE.test(text[i + 1] ?? " ") &&
      !UPPER_RE.test(text[i - 1] ?? "");
    if (sentenceEnd) {
      return i + 1;
    }
    i--;
  }
  return 0;
}

function cleanLongForm(text: string): string {
  return text.replace(SPACES_RE, " ").replace(EDGE_PUNCT_RE, "").trim();
}

/** Does a sentence (or line) start at `index`? */
function startsSentence(text: string, index: number): boolean {
  let i = index - 1;
  while (i >= 0 && (text[i] === " " || text[i] === "\t")) {
    i--;
  }
  return i < 0 || text[i] === "\n" || SENTENCE_END_RE.test(text[i]);
}

/**
 * "Response time (RT) was recorded" defines "response time": a capital that only marks
 * the start of the sentence is dropped. A long form with capitals beyond its first
 * letter — "World Health Organization" — is a name and keeps them.
 */
function sentenceCase(longForm: string, atSentenceStart: boolean): string {
  const rest = longForm.slice(1);
  if (!atSentenceStart || UPPER_RE.test(rest)) {
    return longForm;
  }
  return longForm[0].toLowerCase() + rest;
}

function alnumLength(text: string): number {
  let n = 0;
  for (const ch of text) {
    if (ALNUM_RE.test(ch)) {
      n++;
    }
  }
  return n;
}

/** "response time (RT)" / "(response time [RT])": the long form precedes the bracket. */
function longFirst(
  text: string,
  open: number,
  short: AbbreviationOccurrence
): AbbreviationDefinition | null {
  const from = windowStart(text, open);
  const words = [...text.slice(from, open).matchAll(WORD_IN_WINDOW_RE)].map(
    (m) => ({ start: from + m.index, text: m[0] })
  );
  const surface = text.slice(short.start, short.end);
  const size = alnumLength(surface);
  const reach = Math.min(size + 5, size * 2);
  const window = words.slice(-reach);
  const candidate = window.map((word) => word.text).join(" ");
  const longForm = bestLongForm(surface, candidate);
  if (!longForm || longForm.length <= surface.length) {
    return null;
  }
  const first = window.at(-longForm.split(" ").length);
  if (!first) {
    return null;
  }
  const cleaned = sentenceCase(
    cleanLongForm(text.slice(first.start, open)),
    startsSentence(text, first.start)
  );
  if (cleaned === "" || cleaned.toUpperCase() === short.base.toUpperCase()) {
    return null;
  }
  return {
    start: first.start,
    end: closingIndex(text, open) + 1,
    abbr: { start: short.start, end: short.end },
    longForm: cleaned,
  };
}

/** "WHO (World Health Organization)": the abbreviation precedes the bracket. */
function abbreviationFirst(
  text: string,
  open: number,
  close: number,
  short: AbbreviationOccurrence
): AbbreviationDefinition | null {
  const inner = text
    .slice(open + 1, close)
    .split(LIST_SEPARATOR_RE)[0]
    .trim();
  const words = inner.split(" ").filter(Boolean);
  if (words.length < 2) {
    return null;
  }
  const surface = text.slice(short.start, short.end);
  const longForm = bestLongForm(surface, inner);
  if (!longForm || words.length - longForm.split(" ").length > 1) {
    return null;
  }
  return {
    start: short.start,
    end: close + 1,
    abbr: { start: short.start, end: short.end },
    longForm: cleanLongForm(longForm),
  };
}

interface Occurrences {
  byStart: ReadonlyMap<number, AbbreviationOccurrence>;
  byEnd: ReadonlyMap<number, AbbreviationOccurrence>;
}

/** The definition (if any) made by the bracket pair opening at `open`. */
function definitionAt(
  text: string,
  open: number,
  occurrences: Occurrences
): AbbreviationDefinition | null {
  const close = closingIndex(text, open);
  if (close === -1) {
    return null;
  }
  let first = open + 1;
  while (first < close && SPACE_RE.test(text[first])) {
    first++;
  }
  const inside = occurrences.byStart.get(first);
  const after = inside ? text.slice(inside.end, close).trim() : "";
  const alone = after === "" || LIST_SEPARATOR_RE.test(after[0]);
  const longFormFirst = inside && alone ? longFirst(text, open, inside) : null;
  if (longFormFirst) {
    return longFormFirst;
  }
  let before = open - 1;
  while (before > 0 && SPACE_RE.test(text[before])) {
    before--;
  }
  const outside = occurrences.byEnd.get(before + 1);
  return outside ? abbreviationFirst(text, open, close, outside) : null;
}

function findDefinitions(
  text: string,
  limit: number,
  occurrences: AbbreviationOccurrence[]
): Map<string, AbbreviationDefinition[]> {
  const index: Occurrences = {
    byStart: new Map(occurrences.map((o) => [o.start, o])),
    byEnd: new Map(occurrences.map((o) => [o.end, o])),
  };
  const definitions = new Map<string, AbbreviationDefinition[]>();
  for (let open = 0; open < limit; open++) {
    if (text[open] !== "(" && text[open] !== "[") {
      continue;
    }
    const definition = definitionAt(text, open, index);
    const owner = definition && index.byStart.get(definition.abbr.start);
    if (definition && owner) {
      definitions.set(owner.base, [
        ...(definitions.get(owner.base) ?? []),
        definition,
      ]);
    }
  }
  return definitions;
}

/* ---------------------------------------------------------------------- check */

function escapeRegex(text: string): string {
  return text.replace(REGEX_SPECIAL_RE, "\\$&");
}

function respellings(
  text: string,
  limit: number,
  definitions: AbbreviationDefinition[]
): TextSpan[] {
  const [first] = definitions;
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}])${escapeRegex(first.longForm).replace(SPACES_RE, "\\s+")}(?![\\p{L}\\p{N}])`,
    "giu"
  );
  const spans: TextSpan[] = [];
  for (const match of text.slice(0, limit).matchAll(pattern)) {
    const start = match.index;
    const end = start + match[0].length;
    const inDefinition = definitions.some(
      (definition) => start < definition.end && end > definition.start
    );
    if (start >= first.end && !inDefinition) {
      spans.push({ start, end });
    }
  }
  return spans;
}

function skipReason(
  base: string,
  options: AbbreviationOptions,
  defined: boolean
): SkipReason | null {
  const upper = base.toUpperCase();
  if (options.ignore.has(upper)) {
    return "ignored";
  }
  if (options.skipCommon && DICTIONARY_ABBREVIATIONS.includes(upper)) {
    return "dictionary";
  }
  if (options.skipCommon && STATISTICAL_ABBREVIATIONS.includes(upper)) {
    return "statistic";
  }
  return ROMAN_RE.test(base) && !defined ? "roman" : null;
}

function issuesFor(entry: Omit<AbbreviationEntry, "issues">) {
  const issues: AbbreviationIssue[] = [];
  const [first] = entry.definitions;
  if (!first) {
    return ["undefined" as const];
  }
  if (entry.uses.some((use) => use.start < first.start)) {
    issues.push("before-definition");
  }
  if (entry.definitions.length > 1) {
    issues.push("multiple-definitions");
  }
  if (entry.usesAfterDefinition < MIN_USES) {
    issues.push("few-uses");
  }
  if (entry.respelled.length > 0) {
    issues.push("respelled");
  }
  return issues;
}

export function checkAbbreviations(
  text: string,
  options: AbbreviationOptions
): AbbreviationReport {
  const reference = options.ignoreReferences ? findReferenceList(text) : null;
  const limit = reference?.start ?? text.length;
  const occurrences = scanOccurrences(text, limit);
  const definitions = findDefinitions(text, limit, occurrences);
  const definitionTokens = new Set(
    [...definitions.values()].flat().map((definition) => definition.abbr.start)
  );
  const grouped = new Map<string, AbbreviationOccurrence[]>();
  for (const occurrence of occurrences) {
    grouped.set(occurrence.base, [
      ...(grouped.get(occurrence.base) ?? []),
      occurrence,
    ]);
  }
  const entries: AbbreviationEntry[] = [];
  const skipped: SkippedAbbreviation[] = [];
  for (const [abbr, all] of grouped) {
    const defs = (definitions.get(abbr) ?? []).sort(
      (a, b) => a.start - b.start
    );
    const reason = skipReason(abbr, options, defs.length > 0);
    if (reason) {
      skipped.push({ abbr, reason, count: all.length });
      continue;
    }
    const uses = all.filter((o) => !definitionTokens.has(o.start));
    const firstEnd = defs[0]?.end ?? Number.POSITIVE_INFINITY;
    const entry = {
      abbr,
      uses,
      definitions: defs,
      usesAfterDefinition: uses.filter((use) => use.start >= firstEnd).length,
      respelled: defs.length > 0 ? respellings(text, limit, defs) : [],
    };
    entries.push({ ...entry, issues: issuesFor(entry) });
  }
  const firstSeen = (entry: AbbreviationEntry) =>
    Math.min(
      entry.uses[0]?.start ?? Number.POSITIVE_INFINITY,
      entry.definitions[0]?.start ?? Number.POSITIVE_INFINITY
    );
  entries.sort((a, b) => firstSeen(a) - firstSeen(b));
  return { entries, skipped };
}

/* ------------------------------------------------------- list of abbreviations */

export interface AbbreviationListRow {
  abbr: string;
  meaning: string;
}

/** The thesis "List of Abbreviations": every defined abbreviation, alphabetised. */
export function abbreviationList(
  report: AbbreviationReport
): AbbreviationListRow[] {
  return report.entries
    .filter((entry) => entry.definitions.length > 0)
    .map((entry) => ({
      abbr: entry.abbr,
      meaning: entry.definitions[0].longForm,
    }))
    .sort((a, b) =>
      a.abbr.localeCompare(b.abbr, "en", { sensitivity: "base", numeric: true })
    );
}

/** The list as tab-separated lines, for pasting into a document. */
export function abbreviationListText(
  rows: readonly AbbreviationListRow[]
): string {
  return rows.map((row) => `${row.abbr}\t${row.meaning}`).join("\n");
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/** The list as an HTML table, so a paste into Word or Google Docs arrives as a table. */
export function abbreviationListHtml(
  rows: readonly AbbreviationListRow[],
  head: readonly [string, string]
): string {
  const cells = rows
    .map(
      (row) =>
        `<tr><td>${escapeHtml(row.abbr)}</td><td>${escapeHtml(row.meaning)}</td></tr>`
    )
    .join("");
  return `<table><thead><tr><th>${escapeHtml(head[0])}</th><th>${escapeHtml(head[1])}</th></tr></thead><tbody>${cells}</tbody></table>`;
}

/** The list as CSV (RFC 4180 quoting). */
export function abbreviationListCsv(
  rows: readonly AbbreviationListRow[],
  head: readonly [string, string]
): string {
  const quote = (cell: string) => `"${cell.replaceAll('"', '""')}"`;
  return [head, ...rows.map((row) => [row.abbr, row.meaning] as const)]
    .map((cells) => cells.map(quote).join(","))
    .join("\r\n");
}
